import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash';
import { finalize } from 'rxjs';
import { ManagedUserPermissionsForm, ManagedUserProfileForm } from '../../../core/forms/managed-user.form';
import { ManagedUserInterface } from '../../../core/models/managed-user.model';
import { UserPermissionOptionsInterface } from '../../../core/models/user-permissions.model';
import { TagComponent } from '../../../shared/components/tag/tag.component';
import { ImageSizeEnum } from '../../../shared/pipes/resize-image/resize-image.pipe';
import { SharedModule } from '../../../shared/shared.module';
import { OkCancelModalComponent } from '../../../ui-elements/generic-modal/ok-cancel-modal/ok-cancel-modal.component';
import { ToastService } from '../../../ui-elements/toast/toast.service';
import { ProfileFormService } from '../../../user/profile-form/profile-form.service';
import { ManagedUserProfileComponent } from '../../components/managed-user-profile/managed-user-profile.component';
import { UserPermissionsComponent } from '../../components/user-permissions/user-permissions.component';
import { ManagedUsersService, UpdateManagedUserInterface } from '../../services/managed-users.service';

@Component({
  selector: 'app-edit-managed-user-modal',
  imports: [SharedModule, ManagedUserProfileComponent, TagComponent, UserPermissionsComponent],
  templateUrl: './edit-managed-user-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ProfileFormService],
})
export class EditManagedUserModalComponent implements OnInit {
  private managedUsersService = inject(ManagedUsersService);
  private translator = inject(TranslateService);
  private toastService = inject(ToastService);
  private modalService = inject(NgbModal);
  private activeModal = inject(NgbActiveModal);

  @Input() user: ManagedUserInterface;
  @Input() username: string;
  @Input() permissionOptions: UserPermissionOptionsInterface;
  @Output() saved = new EventEmitter<void>();

  profileForm: ManagedUserProfileForm;
  initialProfileForm: ManagedUserProfileForm;
  permissionsForm: ManagedUserPermissionsForm;
  initialPermissionsForm: ManagedUserPermissionsForm;
  activeTab: 'information' | 'permissions' = 'information';
  imageSizes = ImageSizeEnum;
  savingInProgress = false;

  ngOnInit(): void {
    this.profileForm = this.managedUsersService.getUserProfileForm(this.user);
    this.initialProfileForm = this.managedUsersService.getUserProfileForm(this.user);

    this.permissionsForm = this.managedUsersService.getUserPermissionsForm(this.user.permissions, this.permissionOptions.informationPages);
    this.initialPermissionsForm = this.managedUsersService.getUserPermissionsForm(
      this.user.permissions,
      this.permissionOptions.informationPages,
    );
  }

  onClose = () => {
    if (this.savingInProgress) {
      return;
    }

    if (!this.modificationsWereMade) {
      this.activeModal.dismiss();
      return;
    }

    this.translator
      .get(['MANAGED_USERS.UNSAVED_CHANGES', 'MANAGED_USERS.UNSAVED_CHANGES_INFO', 'MANAGED_USERS.DONT_SAVE', 'ACTIONS.SAVE'])
      .subscribe((translations: { [key: string]: string }) => {
        const [headerText, bodyText, dontSaveBtn, saveBtn] = Object.values(translations);

        const modalRef = this.modalService.open(OkCancelModalComponent, {
          size: 'lg',
          centered: true,
          backdrop: 'static',
          keyboard: false,
        });

        const instance = modalRef.componentInstance as OkCancelModalComponent;
        instance.buttonTexts = [dontSaveBtn, saveBtn];
        instance.buttonClasses = 'mw-10r text-nowrap no-outline';
        instance.headerContent = headerText;
        instance.bodyContent = bodyText;

        instance.cancel.subscribe(() => {
          this.activeModal.dismiss();
        });

        instance.ok.subscribe(() => {
          this.onSave();
        });
      });
  };

  onSave(): void {
    if (this.savingInProgress) {
      return;
    }

    this.profileForm.markAllAsTouched();
    this.profileForm.updateValueAndValidity();

    if (!this.profileForm.valid) {
      this.activeTab = 'information';

      return;
    }

    const formattedPermissionsForUpdate = this.managedUsersService.formatPermissionsForUpdate(
      this.permissionsForm.getRawValue(),
      this.permissionOptions,
    );
    const userDataForUpdate: UpdateManagedUserInterface = {
      ...this.profileForm.value,
      permissions: formattedPermissionsForUpdate,
    };

    this.managedUsersService
      .update(this.user.id, userDataForUpdate)
      .pipe(
        finalize(() => {
          this.savingInProgress = false;
        }),
      )
      .subscribe({
        next: () => {
          this.saved.emit();
          this.activeModal.dismiss();
        },
        error: (error: HttpErrorResponse) => {
          const permissionsErrors = error.error?.errors?.children?.permissions?.children;

          if (permissionsErrors?.granted?.errors || permissionsErrors?.informationPages?.errors) {
            this.activeTab = 'permissions';
            this.toastService.danger(this.translator.instant('MANAGED_USERS.PERMISSIONS_NO_LONGER_AVAILABLE'));
            this.managedUsersService.getPermissionOptions().subscribe((permissionOptions) => {
              this.permissionOptions = permissionOptions;
            });
          }
        },
      });
  }

  get informationTabActive(): boolean {
    return this.activeTab === 'information';
  }

  get modificationsWereMade(): boolean {
    const profileFormWasModified = !isEqual(this.profileForm.getRawValue(), this.initialProfileForm.getRawValue());
    const permissionsFormWasModified = !isEqual(this.permissionsForm.getRawValue(), this.initialPermissionsForm.getRawValue());

    return profileFormWasModified || permissionsFormWasModified;
  }
}
